import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { currencySchema, CurrencyFormData } from '../schemas/master-data.schemas';
import { Currency } from '../types/master-data.types';
import { Loader2 } from 'lucide-react';

interface CurrencyFormProps {
  currency?: Currency;
  onSubmit: (data: CurrencyFormData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export function CurrencyForm({ currency, onSubmit, onCancel, isLoading }: CurrencyFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<CurrencyFormData>({
    resolver: zodResolver(currencySchema),
    defaultValues: currency
      ? {
          code: currency.code,
          name: currency.name,
          symbol: currency.symbol,
          exchangeRate: currency.exchangeRate,
          isBase: currency.isBase,
          sapCode: currency.sapCode ?? undefined,
        }
      : {
          code: '',
          name: '',
          symbol: '',
          exchangeRate: 1,
          isBase: false,
          sapCode: undefined,
        },
  });

  const isBase = watch('isBase');

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="code">Código (ISO) *</Label>
          <Input
            id="code"
            placeholder="USD"
            maxLength={3}
            {...register('code')}
            error={errors.code?.message}
            disabled={isLoading || !!currency}
            className="uppercase"
          />
          <p className="text-xs text-muted-foreground">Código de 3 letras</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="symbol">Símbolo *</Label>
          <Input
            id="symbol"
            placeholder="$"
            maxLength={5}
            {...register('symbol')}
            error={errors.symbol?.message}
            disabled={isLoading}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="name">Nombre *</Label>
        <Input
          id="name"
          placeholder="Dólar Estadounidense"
          {...register('name')}
          error={errors.name?.message}
          disabled={isLoading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="exchangeRate">Tasa de Cambio *</Label>
        <Input
          id="exchangeRate"
          type="number"
          step="0.0001"
          placeholder="1.0000"
          {...register('exchangeRate', { valueAsNumber: true })}
          error={errors.exchangeRate?.message}
          disabled={isLoading || isBase}
        />
        <p className="text-xs text-muted-foreground">
          {isBase ? 'La divisa base siempre tiene tasa 1.0' : 'Tasa relativa a la divisa base'}
        </p>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="isBase"
          checked={isBase}
          onCheckedChange={(checked) => {
            setValue('isBase', checked as boolean);
            if (checked) {
              setValue('exchangeRate', 1);
            }
          }}
          disabled={isLoading}
        />
        <Label htmlFor="isBase" className="cursor-pointer">
          Establecer como divisa base
        </Label>
      </div>

      <div className="space-y-2">
        <Label htmlFor="sapCode">Código SAP</Label>
        <Input
          id="sapCode"
          placeholder="Código equivalente en SAP (opcional)"
          maxLength={64}
          {...register('sapCode')}
          error={(errors as any).sapCode?.message}
          disabled={isLoading}
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Guardando...
            </>
          ) : currency ? (
            'Actualizar'
          ) : (
            'Crear'
          )}
        </Button>
      </div>
    </form>
  );
}

export default CurrencyForm;
